import React from 'react';
import { GetStaticProps, GetStaticPaths, NextPage } from 'next';
import logger from 'helpers/logger';
import { getPageContent } from 'application/repositories/pageContentRepository';
import { getNavigationData } from 'application/repositories/navigationRepository';
import { PageTypeMapFeature } from 'features/Mappers/PageType/PageTypeFeature';
import { PageContentResponse } from 'application/repositories/pageContentRepository';
import { LayoutFeature } from 'features/Layouts/Layout/LayoutFeature';
import { getContactFormData, getSignupFormData } from 'application/repositories/formRepository';
import { ModalSignupFormFeature } from 'features/Modules/ModalSignupForm';
import { ContactFormFeature } from 'features/Modules/ContactForm';
import { getLanguages } from 'application/repositories/languagesRepository';
import { getLocations } from 'application/repositories/locationsRepository';

const Page: NextPage<PageContentResponse> = (pageContent) => {
	logger.info('Rendering with ISR (incremental static regeneration)');

	const { page, accessToken, contactFormData, contactLink, signupFormData, culture, host } = pageContent ?? {};
	return (
		<LayoutFeature {...pageContent} accessToken={accessToken}>
			<PageTypeMapFeature {...page} accessToken={accessToken} culture={culture} />
			<ContactFormFeature accessToken={accessToken} culture={culture} host={host} {...contactFormData} contactLink={contactLink} />
			<ModalSignupFormFeature accessToken={accessToken} culture={culture} host={host} {...signupFormData} />
		</LayoutFeature>
	);
};

export const getStaticPaths: GetStaticPaths = async () => {
	// Return empty paths to enable fallback for all routes
	return {
		paths: [],
		fallback: 'blocking',
	};
};

export const getStaticProps: GetStaticProps<any> = async ({ params, preview, previewData: untypedPreviewData, locale }) => {
	const { slug, origin } = params;

	const slugArr = slug as string[];
	const prefetch = slugArr?.includes('_sites');
	let path = '';

	if (!prefetch) {
		path = slug ? slugArr.join('/') : '';
	} else {
		path = slug ? slugArr.slice(3).join('/') : '';
	}
	const host = atob(origin as string);

	const previewData = untypedPreviewData as Models.PreviewData;
	try {
		const culture = locale === 'default' ? 'en' : locale;
		// Static pages cannot check authentication at build time
		const restrictedUrls = ['my-account', 'basket', 'delivery', 'information', 'payment', 'receipt'];
		const restrictedPage = restrictedUrls.includes(path);

		// Skip restricted pages - they should use SSR instead
		if (restrictedPage) {
			return {
				notFound: true,
			};
		}

		const contentPromise = getPageContent({
			url: path,
			host,
			preview,
			previewData,
			locale,
		});

		const contactFormData = await getContactFormData({
			host,
			locale: culture,
		});

		const navigationData = await getNavigationData({
			host,
			locale: culture,
		});

		const signupFormData = await getSignupFormData({
			host,
			locale: culture,
		});

		const languageSelector = await getLanguages({
			host,
			locale: culture,
		});
		const locations = await getLocations({ host, locale: culture });
		const content = await contentPromise;

		return {
			props: {
				content,
				host,
				navigationData,
				contactFormData,
				signupFormData,
				culture,
				languageSelector,
				locations,
			},
			revalidate: 60,
		};
	} catch (error) {
		const { statusCode, url } = error;
		if (statusCode === 404) {
			return {
				notFound: true,
			};
		}

		if (statusCode === 301 || statusCode === 302 || statusCode === 307 || statusCode === 308) {
			return {
				redirect: {
					statusCode: statusCode,
					destination: url,
				},
			};
		}

		logger.error(error);
		throw new Error(error);
	}
};

export default Page;
